
import { useState, useCallback, useEffect } from 'react';
import { api } from '@/lib/api';

export interface CategoryKeywords {
  [category: string]: string[];
}

export interface AppSettings {
  categoryKeywords: CategoryKeywords;

  // Branding
  appName?: string;
  appLogoUrl?: string | null;
  appIconUrl?: string | null;
  feedbackFormUrl?: string;
  fromEmail?: string;

  // Integrations
  clearstreamEndpoint?: string;
  clearstreamApiKey?: string;
  clearstreamWebhookSecret?: string;
  /** Read-only: true when Clearstream API key is set (outbound SMS configured) */
  clearstreamConfigured?: boolean;

  // Email integration (provider-agnostic)
  emailProvider?: string;
  emailApiKey?: string;
  emailDomain?: string;

  autoBlockEnabled?: boolean;
  autoBlockReportThreshold?: number;

  trainingSignupEnabled?: boolean;
  trainingSignupConfigured?: boolean;

  /** When false, hide responses feature; default true when unset. */
  responsesEnabled?: boolean;

  /** When true, show the stage tablet queue. Default false when unset. */
  stageEnabled?: boolean;
}

const DEFAULT_SETTINGS: AppSettings = {
  categoryKeywords: {
    health: ['health', 'healing', 'sick', 'illness', 'surgery', 'cancer', 'recovery'],
    family: ['family', 'marriage', 'children', 'spouse', 'parent', 'child', 'son', 'daughter'],
    finances: ['job', 'financial', 'work', 'money', 'employment', 'career'],
    guidance: ['guidance', 'wisdom', 'direction', 'decision', 'discernment'],
    relationships: ['relationship', 'friendship', 'friend', 'reconciliation'],
    spiritual: ['faith', 'spiritual', 'salvation', 'growth', 'peace'],
    gratitude: ['thankful', 'grateful', 'praise', 'blessed', 'testimony'],
  },
  appName: 'One Voice',
  appLogoUrl: null,
  appIconUrl: null,
  feedbackFormUrl: '',
  fromEmail: '',
  clearstreamEndpoint: '',
  clearstreamApiKey: '',
  clearstreamWebhookSecret: '',
  emailProvider: '',
  emailApiKey: '',
  emailDomain: '',
  responsesEnabled: true,
  stageEnabled: false,
};

interface UseSettingsReturn {
  settings: AppSettings;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  fetchSettings: () => Promise<void>;
  updateSettings: (data: Partial<AppSettings>) => Promise<boolean>;
  updateCategoryKeywords: (category: string, keywords: string[]) => Promise<boolean>;
  addCategory: (category: string, keywords?: string[]) => Promise<boolean>;
  removeCategory: (category: string) => Promise<boolean>;
}

export function useSettings(): UseSettingsReturn {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<AppSettings>('/admin/settings');
      if (response.error) {
        // Use default settings if API fails
        console.warn('Failed to fetch settings, using defaults:', response.error);
      } else if (response.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...response.data });
      }
    } catch (err) {
      console.warn('Failed to fetch settings, using defaults:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateSettings = useCallback(
    async (data: Partial<AppSettings>): Promise<boolean> => {
      setIsSaving(true);
      setError(null);
      
      // Optimistic update
      const previousSettings = settings;
      setSettings((prev) => ({ ...prev, ...data }));

      try {
        const response = await api.put<AppSettings>('/admin/settings', data);
        if (response.error) {
          // Revert on error
          setSettings(previousSettings);
          setError(response.error);
          return false;
        }
        if (response.data) {
          setSettings(response.data);
        }
        return true;
      } catch (err) {
        setSettings(previousSettings);
        setError(err instanceof Error ? err.message : 'Failed to update settings');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [settings]
  );

  const updateCategoryKeywords = useCallback(
    async (category: string, keywords: string[]): Promise<boolean> => {
      const newKeywords = {
        ...settings.categoryKeywords,
        [category]: keywords,
      };
      return updateSettings({ categoryKeywords: newKeywords });
    },
    [settings.categoryKeywords, updateSettings]
  );

  const addCategory = useCallback(
    async (category: string, keywords: string[] = []): Promise<boolean> => {
      if (settings.categoryKeywords[category]) {
        setError('Category already exists');
        return false;
      }
      const newKeywords = {
        ...settings.categoryKeywords,
        [category]: keywords,
      };
      return updateSettings({ categoryKeywords: newKeywords });
    },
    [settings.categoryKeywords, updateSettings]
  );

  const removeCategory = useCallback(
    async (category: string): Promise<boolean> => {
      const { [category]: removed, ...remaining } = settings.categoryKeywords;
      return updateSettings({ categoryKeywords: remaining });
    },
    [settings.categoryKeywords, updateSettings]
  );

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    isLoading,
    isSaving,
    error,
    fetchSettings,
    updateSettings,
    updateCategoryKeywords,
    addCategory,
    removeCategory,
  };
}
